import { useTranslation } from 'react-i18next';
import { Bell, BellOff, CircleAlert, LoaderCircle, Send } from 'lucide-react';
import { usePushErrorText } from '@/push/push-error';
import { usePush } from '@/push/use-push';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

function useBlockedReason(state: ReturnType<typeof usePush>['state']): string | null {
  const { t } = useTranslation();
  switch (state) {
    case 'unsupported':
      return t('push.unsupported');
    case 'insecure':
      return t('push.insecure');
    case 'unavailable':
      return t('push.unavailable');
    case 'denied':
      return t('push.denied');
    default:
      return null;
  }
}

export function PushSettings() {
  const { t } = useTranslation();
  const errorText = usePushErrorText();
  const push = usePush();
  const blocked = useBlockedReason(push.state);

  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="size-4" />
          {t('push.title')}
        </CardTitle>
        <CardDescription>{t('push.hint')}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        {blocked ? (
          <p className="flex items-start gap-1.5 text-sm text-muted-foreground">
            <CircleAlert className="mt-0.5 size-4 shrink-0" />
            {blocked}
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {push.state === 'on'
                ? t('push.on', { count: push.devices })
                : t('push.off')}
            </p>

            <div className="flex flex-col gap-2 sm:flex-row">
              {push.state === 'on' ? (
                <>
                  <Button variant="outline" onClick={push.disable} disabled={push.busy}>
                    <BellOff />
                    {t('push.disable')}
                  </Button>
                  <Button variant="ghost" onClick={push.sendTest} disabled={push.busy}>
                    <Send />
                    {t('push.test')}
                  </Button>
                </>
              ) : (
                <Button onClick={push.enable} disabled={push.busy}>
                  {push.busy ? (
                    <LoaderCircle className="size-4 animate-spin" />
                  ) : (
                    <Bell />
                  )}
                  {push.busy ? t('push.enabling') : t('push.enable')}
                </Button>
              )}
            </div>

            {push.testSent && (
              <p className="text-sm text-muted-foreground">{t('push.testSent')}</p>
            )}
          </>
        )}

        {push.error ? (
          <p className="flex items-center gap-1.5 text-sm text-destructive">
            <CircleAlert className="size-4 shrink-0" />
            {errorText(push.error)}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
